import { Box, Button, CircularProgress, Paper, Stack, Typography, useMediaQuery } from '@mui/material';
import ErrorOutlineRounded from '@mui/icons-material/ErrorOutlineRounded';
import ColorLogo from '../Images/ColorLogo.dark.svg';
import { ReactNode } from 'react';

export interface AuthenticationTransitionProps {
  error?: string;
  message?: string;
  onRetry?: () => void;
  onSignOut?: () => void;
}

export interface AuthenticationTransitionBoundaryProps extends AuthenticationTransitionProps {
  children: ReactNode;
  visible: boolean;
  unmountContent?: boolean;
}

const AuthenticationTransition = ({
  error,
  message = 'Securely connecting your account and organization…',
  onRetry,
  onSignOut,
}: AuthenticationTransitionProps) => {
  const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

  return (
    <Box
      data-testid="authentication-transition"
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 2147483647,
        boxSizing: 'border-box',
        minHeight: '100dvh',
        overflowY: 'auto',
        display: 'grid',
        placeItems: 'center',
        px: { xs: 2, sm: 4 },
        pt: 'max(24px, env(safe-area-inset-top))',
        pr: 'max(16px, env(safe-area-inset-right))',
        pb: 'max(24px, env(safe-area-inset-bottom))',
        pl: 'max(16px, env(safe-area-inset-left))',
        color: '#f6f8fb',
        bgcolor: '#071426',
        backgroundImage: 'radial-gradient(circle at 76% 16%, rgba(47, 123, 255, .28), transparent 38%), radial-gradient(circle at 16% 82%, rgba(31, 185, 173, .18), transparent 34%), linear-gradient(145deg, #071426 0%, #0b1d35 58%, #071426 100%)',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: 'min(100%, 460px)',
          boxSizing: 'border-box',
          p: { xs: 3, sm: 4.5 },
          borderRadius: 4,
          border: '1px solid rgba(139, 182, 255, .2)',
          bgcolor: 'rgba(8, 24, 45, .88)',
          color: 'inherit',
          boxShadow: '0 28px 80px rgba(0, 0, 0, .34)',
          backdropFilter: 'blur(18px)',
        }}
      >
        <Stack spacing={3} alignItems="center" textAlign="center">
          <Box component="img" src={ColorLogo} alt="Synergist Pro" sx={{ width: { xs: 210, sm: 250 }, maxWidth: '88%', height: 'auto' }} />

          {error ? (
            <>
              <ErrorOutlineRounded aria-hidden sx={{ fontSize: 46, color: '#8bb6ff' }} />
              <Box role="alert" aria-live="assertive">
                <Typography component="h1" variant="h5" fontWeight={800}>We couldn’t finish signing you in</Typography>
                <Typography sx={{ mt: 1, color: 'rgba(246, 248, 251, .76)' }}>{error}</Typography>
              </Box>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} width="100%">
                <Button fullWidth variant="contained" onClick={onRetry} sx={{ minHeight: 48 }}>Retry</Button>
                <Button fullWidth variant="outlined" onClick={onSignOut} sx={{ minHeight: 48, color: 'inherit', borderColor: 'rgba(246, 248, 251, .45)' }}>Sign out</Button>
              </Stack>
            </>
          ) : (
            <Box role="status" aria-live="polite" aria-label="Preparing your Synergist Pro workspace">
              <CircularProgress
                variant={reduceMotion ? 'determinate' : 'indeterminate'}
                value={reduceMotion ? 72 : undefined}
                size={44}
                thickness={3.5}
                sx={{ color: '#8bb6ff' }}
              />
              <Typography component="h1" variant="h5" fontWeight={800} sx={{ mt: 2.25 }}>Preparing your workspace</Typography>
              <Typography sx={{ mt: 1, color: 'rgba(246, 248, 251, .76)' }}>{message}</Typography>
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
};

export const AuthenticationTransitionBoundary = ({ children, visible, unmountContent = false, ...transition }: AuthenticationTransitionBoundaryProps) => (
  <>
    <div
      data-testid="application-content"
      aria-hidden={visible ? true : undefined}
      style={visible ? { display: 'none' } : undefined}
    >
      {!(visible && unmountContent) && children}
    </div>
    {visible && <AuthenticationTransition {...transition} />}
  </>
);

export default AuthenticationTransition;
